// src/pages/inventory/NewPurchase.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    TextField,
    Grid,
    MenuItem,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    CircularProgress,
    Divider,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    ShoppingCart as ShoppingCartIcon,
    ArrowBack as ArrowBackIcon,
    QrCodeScanner as ScanIcon,
    CheckCircle as CheckIcon,
    Close as CloseIcon
} from '@mui/icons-material';

const NewPurchase = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    const [suppliers, setSuppliers] = useState([]);
    const [products, setProducts] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    
    // Series capturing states
    const [seriesDialogOpen, setSeriesDialogOpen] = useState(false);
    const [tempSeries, setTempSeries] = useState([]); // [{ serial_number, mac_address, condition }]
    const [seriesError, setSeriesError] = useState(null);

    const [purchaseData, setPurchaseData] = useState({
        supplier_id: '',
        purchase_date: new Date().toISOString().split('T')[0],
        reference_number: '',
        status: 'received', // Default to received for direct stock entry
        payment_status: 'pending',
        account_id: '',
        warehouse_id: '',
        notes: '',
        items: []
    });

    const [currentItem, setCurrentItem] = useState({
        item_id: '',
        quantity: 1,
        unit_price: 0,
        notes: '',
        units: [] // array of { serial_number, mac_address, condition }
    });

    const [totalAmount, setTotalAmount] = useState(0);

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        // Calcular el monto total cuando cambian los items
        const total = purchaseData.items.reduce((sum, item) => {
            return sum + (item.quantity * item.unit_price);
        }, 0);
        setTotalAmount(total);
    }, [purchaseData.items]);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            if (!token) {
                navigate('/login');
                return;
            }

            // Fetch suppliers
            const suppliersResponse = await fetch('/api/inventory/suppliers', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!suppliersResponse.ok) {
                if (suppliersResponse.status === 401) {
                    navigate('/login');
                    return;
                }
                throw new Error('Error al obtener proveedores');
            }
            const suppliersData = await suppliersResponse.json();
            setSuppliers(suppliersData);

            // Fetch products
            const productsResponse = await fetch('/api/inventory/items', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!productsResponse.ok) {
                throw new Error('Error al obtener productos');
            }
            const productsData = await productsResponse.json();
            setProducts(productsData);

            // Verificar si hay un producto pre-seleccionado en el estado de navegación
            if (location.state && location.state.prefilledItemId) {
                const prefilledId = location.state.prefilledItemId;
                const selectedProduct = productsData.find(product => product.id === prefilledId);
                if (selectedProduct) {
                    const isSerialized = !!selectedProduct.equipment_type;
                    setCurrentItem(prev => ({
                        ...prev,
                        item_id: prefilledId,
                        unit_price: parseFloat(selectedProduct.purchase_price) || 0,
                        units: isSerialized ? Array.from({ length: prev.quantity }, () => ({ serial_number: '', mac_address: '', condition: 'new' })) : []
                    }));
                }
            }

            // Fetch financial accounts
            const accountsResponse = await fetch('/api/accounting/accounts', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (accountsResponse.ok) {
                const accountsData = await accountsResponse.json();
                setAccounts(accountsData.filter(a => a.status === 'active'));
            }

            // Fetch warehouses
            const warehousesResponse = await fetch('/api/inventory/warehouses', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (warehousesResponse.ok) {
                const warehousesData = await warehousesResponse.json();
                setWarehouses(warehousesData);
                // Pre-select first warehouse as default
                if (warehousesData.length > 0) {
                    setPurchaseData(prev => ({
                        ...prev,
                        warehouse_id: warehousesData[0].id
                    }));
                }
            }

        } catch (error) {
            console.error('Error fetching initial data:', error);
            setError(error.message || 'Error al cargar datos iniciales');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPurchaseData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleItemInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'item_id' && value) {
            const selectedProduct = products.find(product => product.id === value);
            if (selectedProduct) {
                const isSerialized = !!selectedProduct.equipment_type;
                setCurrentItem(prev => ({
                    ...prev,
                    item_id: value,
                    unit_price: parseFloat(selectedProduct.purchase_price) || 0,
                    units: isSerialized ? Array.from({ length: prev.quantity }, () => ({ serial_number: '', mac_address: '', condition: 'new' })) : []
                }));
                return;
            }
        }

        if (name === 'quantity') {
            const qty = Math.max(1, parseInt(value) || 1);
            setCurrentItem(prev => {
                const selectedProduct = products.find(p => p.id === prev.item_id);
                const isSerialized = selectedProduct ? !!selectedProduct.equipment_type : false;
                
                let newUnits = prev.units || [];
                if (isSerialized) {
                    if (newUnits.length < qty) {
                        newUnits = [
                            ...newUnits,
                            ...Array.from({ length: qty - newUnits.length }, () => ({ serial_number: '', mac_address: '', condition: 'new' }))
                        ];
                    } else if (newUnits.length > qty) {
                        newUnits = newUnits.slice(0, qty);
                    }
                }
                
                return {
                    ...prev,
                    quantity: qty,
                    units: isSerialized ? newUnits : []
                };
            });
            return;
        }

        setCurrentItem(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Series capture
    const handleOpenSeriesDialog = () => {
        setTempSeries(currentItem.units && currentItem.units.length === currentItem.quantity 
            ? JSON.parse(JSON.stringify(currentItem.units))
            : Array.from({ length: currentItem.quantity }, () => ({ serial_number: '', mac_address: '', condition: 'new' }))
        );
        setSeriesError(null);
        setSeriesDialogOpen(true);
    };

    const handleTempSeriesChange = (index, field, value) => {
        setTempSeries(prev => prev.map((item, idx) => {
            if (idx === index) {
                return { ...item, [field]: value };
            }
            return item;
        }));
    };

    const handleSaveSeries = () => {
        setSeriesError(null);
        const serials = new Set();
        const macs = new Set();

        for (let i = 0; i < tempSeries.length; i++) {
            const u = tempSeries[i];
            const cleanSN = u.serial_number.toUpperCase().trim();
            const cleanMAC = u.mac_address.toUpperCase().trim();

            if (!cleanSN || !cleanMAC) {
                setSeriesError(`Por favor complete todos los campos de S/N y MAC (Fila #${i + 1}).`);
                return;
            }

            if (serials.has(cleanSN)) {
                setSeriesError(`Número de serie duplicado localmente: ${cleanSN}`);
                return;
            }
            if (macs.has(cleanMAC)) {
                setSeriesError(`Dirección MAC duplicada localmente: ${cleanMAC}`);
                return;
            }

            serials.add(cleanSN);
            macs.add(cleanMAC);
        }

        setCurrentItem(prev => ({
            ...prev,
            units: tempSeries.map(u => ({
                serial_number: u.serial_number.toUpperCase().trim(),
                mac_address: u.mac_address.toUpperCase().trim(),
                condition: u.condition || 'new'
            }))
        }));

        setSeriesDialogOpen(false);
    };

    const addItemToPurchase = () => {
        if (!currentItem.item_id || currentItem.quantity <= 0 || currentItem.unit_price <= 0) {
            setError('Por favor complete todos los campos requeridos del producto');
            return;
        }

        const selectedProduct = products.find(product => product.id === currentItem.item_id);
        const isSerialized = selectedProduct ? !!selectedProduct.equipment_type : false;

        // Validar series configuradas si es serializado
        if (isSerialized && purchaseData.status === 'received') {
            const isConfigured = currentItem.units && 
                                currentItem.units.length === currentItem.quantity && 
                                currentItem.units.every(u => u.serial_number && u.mac_address);
            if (!isConfigured) {
                setError('Debe registrar todos los Números de Serie y MAC antes de agregar este producto.');
                return;
            }
        }

        const newItem = {
            ...currentItem,
            item_name: selectedProduct ? `${selectedProduct.brand || ''} ${selectedProduct.name}` : 'Producto desconocido',
            equipment_type: selectedProduct ? selectedProduct.equipment_type : null,
            subtotal: currentItem.quantity * currentItem.unit_price
        };

        setPurchaseData(prev => ({
            ...prev,
            items: [...prev.items, newItem]
        }));

        // Reset current item form
        setCurrentItem({
            item_id: '',
            quantity: 1,
            unit_price: 0,
            notes: '',
            units: []
        });
        setError(null);
    };

    const removeItem = (index) => {
        setPurchaseData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async () => {
        if (!purchaseData.supplier_id) {
            setError('Por favor seleccione un proveedor');
            return;
        }

        if (!purchaseData.warehouse_id) {
            setError('Por favor seleccione un almacén de destino');
            return;
        }

        if (purchaseData.payment_status === 'paid' && !purchaseData.account_id) {
            setError('Por favor seleccione una Cuenta de Pago para registrar el egreso contable');
            return;
        }

        if (purchaseData.items.length === 0) {
            setError('Debe agregar al menos un producto a la orden de compra');
            return;
        }

        try {
            setSubmitting(true);
            setError(null);
            const token = localStorage.getItem('token');

            if (!token) {
                navigate('/login');
                return;
            }

            const submitData = {
                ...purchaseData,
                total_amount: totalAmount
            };

            const response = await fetch('/api/inventory/purchases', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(submitData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al crear la orden de compra');
            }

            setSuccess('Orden de compra creada exitosamente');
            setTimeout(() => {
                navigate('/inventory/purchases');
            }, 2000);

        } catch (error) {
            console.error('Error creating purchase:', error);
            setError(error.message || 'Error al crear la orden de compra');
        } finally {
            setSubmitting(false);
        }
    };

    const selectedProduct = products.find(p => p.id === currentItem.item_id);
    const isSerialized = selectedProduct ? !!selectedProduct.equipment_type : false;
    const isSeriesConfigured = !isSerialized || (purchaseData.status !== 'received') || (
        currentItem.units && 
        currentItem.units.length === currentItem.quantity && 
        currentItem.units.every(u => u.serial_number && u.mac_address)
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton sx={{ mr: 2 }} onClick={() => navigate('/inventory/purchases')}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    <ShoppingCartIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '2.5rem', color: 'primary.main' }} />
                    Facturación de Compra Inteligente
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    {success}
                </Alert>
            )}

            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium', color: 'text.secondary' }}>
                    1. Información del Comprobante
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            select
                            fullWidth
                            required
                            label="Proveedor"
                            name="supplier_id"
                            value={purchaseData.supplier_id}
                            onChange={handleInputChange}
                            disabled={submitting}
                        >
                            <MenuItem value="">Seleccione un proveedor</MenuItem>
                            {suppliers.map((supplier) => (
                                <MenuItem key={supplier.id} value={supplier.id}>
                                    {supplier.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            select
                            fullWidth
                            required
                            label="Almacén de Destino (Stock)"
                            name="warehouse_id"
                            value={purchaseData.warehouse_id}
                            onChange={handleInputChange}
                            disabled={submitting}
                        >
                            <MenuItem value="">Seleccione un almacén</MenuItem>
                            {warehouses.map((wh) => (
                                <MenuItem key={wh.id} value={wh.id}>
                                    {wh.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Fecha de Factura"
                            name="purchase_date"
                            value={purchaseData.purchase_date}
                            onChange={handleInputChange}
                            InputLabelProps={{ shrink: true }}
                            disabled={submitting}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            label="Número de Factura / Referencia"
                            name="reference_number"
                            value={purchaseData.reference_number}
                            onChange={handleInputChange}
                            placeholder="Ej: F001-0002345"
                            disabled={submitting}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            select
                            fullWidth
                            label="Estado de Recepción"
                            name="status"
                            value={purchaseData.status}
                            onChange={handleInputChange}
                            disabled={submitting}
                        >
                            <MenuItem value="pending">Pendiente (No ingresa stock aún)</MenuItem>
                            <MenuItem value="received">Recibido (Ingresa stock inmediatamente)</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            select
                            fullWidth
                            label="Estado del Pago"
                            name="payment_status"
                            value={purchaseData.payment_status}
                            onChange={handleInputChange}
                            disabled={submitting}
                        >
                            <MenuItem value="pending">Pendiente (Cuenta por Pagar)</MenuItem>
                            <MenuItem value="paid">Pagado (Registra Egreso Financiero)</MenuItem>
                        </TextField>
                    </Grid>

                    {purchaseData.payment_status === 'paid' && (
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                select
                                fullWidth
                                required={purchaseData.payment_status === 'paid'}
                                label="Cuenta Financiera de Egreso"
                                name="account_id"
                                value={purchaseData.account_id || ''}
                                onChange={handleInputChange}
                                disabled={submitting}
                            >
                                <MenuItem value="">Seleccione cuenta de egreso</MenuItem>
                                {accounts.map((account) => (
                                    <MenuItem key={account.id} value={account.id}>
                                        {account.name} (Saldo: $ {parseFloat(account.current_balance || 0).toFixed(2)})
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Notas de la Compra"
                            name="notes"
                            value={purchaseData.notes}
                            onChange={handleInputChange}
                            placeholder="Detalles adicionales sobre esta orden de compra o factura"
                            disabled={submitting}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium', color: 'text.secondary' }}>
                    2. Agregar Productos a la Factura
                </Typography>

                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            fullWidth
                            required
                            label="Producto"
                            name="item_id"
                            value={currentItem.item_id}
                            onChange={handleItemInputChange}
                            disabled={submitting}
                        >
                            <MenuItem value="">Seleccione un producto</MenuItem>
                            {products.map((product) => (
                                <MenuItem key={product.id} value={product.id}>
                                    {product.name} ({product.sku || 'Sin SKU'}) {product.equipment_type ? `[${product.equipment_type}]` : ''}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            required
                            type="number"
                            label="Cantidad"
                            name="quantity"
                            value={currentItem.quantity}
                            onChange={handleItemInputChange}
                            InputProps={{ inputProps: { min: 1 } }}
                            disabled={submitting}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            required
                            type="number"
                            label="Precio Unitario"
                            name="unit_price"
                            value={currentItem.unit_price}
                            onChange={handleItemInputChange}
                            InputProps={{
                                inputProps: { min: 0, step: 0.01 },
                                startAdornment: '$'
                            }}
                            disabled={submitting}
                        />
                    </Grid>

                    {isSerialized && purchaseData.status === 'received' && (
                        <Grid item xs={12} md={3}>
                            <Button
                                variant="outlined"
                                color={currentItem.units && currentItem.units.every(u => u.serial_number && u.mac_address) ? "success" : "warning"}
                                startIcon={<ScanIcon />}
                                onClick={handleOpenSeriesDialog}
                                fullWidth
                                sx={{ height: '56px', borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                            >
                                {currentItem.units && currentItem.units.every(u => u.serial_number && u.mac_address) 
                                    ? `Series Listas (${currentItem.quantity})`
                                    : `Configurar Series (${currentItem.quantity})`}
                            </Button>
                        </Grid>
                    )}

                    {!isSerialized && (
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Notas del Producto"
                                name="notes"
                                value={currentItem.notes}
                                onChange={handleItemInputChange}
                                disabled={submitting}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={addItemToPurchase}
                            disabled={submitting || !currentItem.item_id || !isSeriesConfigured}
                            sx={{ height: '56px', width: '100%' }}
                        >
                            Agregar
                        </Button>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium', color: 'text.secondary' }}>
                    Productos en el Comprobante ({purchaseData.items.length})
                </Typography>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Producto</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Cantidad</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Precio Unitario</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Subtotal</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acción</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {purchaseData.items.length > 0 ? (
                                purchaseData.items.map((item, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                {item.item_name}
                                            </Typography>
                                            {item.notes && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Nota: {item.notes}
                                                </Typography>
                                            )}
                                            {item.units && item.units.length > 0 && (
                                                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {item.units.map((u, uidx) => (
                                                        <Tooltip title={`MAC: ${u.mac_address}`} key={uidx}>
                                                            <Chip 
                                                                label={`S/N: ${u.serial_number}`} 
                                                                size="small" 
                                                                variant="outlined" 
                                                                color="primary"
                                                                sx={{ fontFamily: 'monospace', fontSize: '0.75rem', height: '20px' }}
                                                            />
                                                        </Tooltip>
                                                    ))}
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip label={item.quantity} size="small" sx={{ fontWeight: 'bold' }} />
                                        </TableCell>
                                        <TableCell align="right">${parseFloat(item.unit_price).toFixed(2)}</TableCell>
                                        <TableCell align="right">${(item.quantity * item.unit_price).toFixed(2)}</TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                color="error"
                                                size="small"
                                                onClick={() => removeItem(index)}
                                                disabled={submitting}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                        No hay productos agregados a la orden
                                    </TableCell>
                                </TableRow>
                            )}
                            {purchaseData.items.length > 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} align="right">
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            Total Factura:
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="h6" fontWeight="bold" color="primary.main">
                                            ${totalAmount.toFixed(2)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                    variant="outlined"
                    onClick={() => navigate('/inventory/purchases')}
                    disabled={submitting}
                    sx={{ px: 4 }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSubmit}
                    disabled={submitting || purchaseData.items.length === 0}
                    sx={{ px: 4, fontWeight: 'bold' }}
                >
                    {submitting ? 'Registrando...' : 'Registrar Factura de Compra'}
                </Button>
            </Box>

            {/* DIALOG DE CAPTURA DE SERIES (NÚMEROS DE SERIE / MACS) */}
            <Dialog open={seriesDialogOpen} onClose={() => setSeriesDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ScanIcon />
                        Configurar Números de Serie y MAC
                    </Typography>
                    <IconButton onClick={() => setSeriesDialogOpen(false)} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    {seriesError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {seriesError}
                        </Alert>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Ingrese o escanee el número de serie y la dirección MAC para cada una de las <strong>{currentItem.quantity}</strong> unidades del equipo.
                    </Typography>

                    <Box sx={{ maxHeight: '400px', overflowY: 'auto', pr: 1 }}>
                        {tempSeries.map((unit, idx) => (
                            <Grid container spacing={2} key={idx} alignItems="center" sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                <Grid item xs={1} sm={0.5} sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                                        #{idx + 1}
                                    </Typography>
                                </Grid>
                                
                                <Grid item xs={11} sm={5}>
                                    <TextField
                                        fullWidth
                                        required
                                        size="small"
                                        label="Número de Serie (S/N)"
                                        placeholder="Ej. SN1029384756"
                                        value={unit.serial_number}
                                        onChange={(e) => handleTempSeriesChange(idx, 'serial_number', e.target.value)}
                                        inputProps={{ style: { textTransform: 'uppercase' } }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        required
                                        size="small"
                                        label="Dirección MAC"
                                        placeholder="Ej. A0:B1:C2:D3:E4:F5"
                                        value={unit.mac_address}
                                        onChange={(e) => handleTempSeriesChange(idx, 'mac_address', e.target.value)}
                                        inputProps={{ style: { textTransform: 'uppercase' } }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={2.5}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Condición"
                                        value={unit.condition || 'new'}
                                        onChange={(e) => handleTempSeriesChange(idx, 'condition', e.target.value)}
                                    >
                                        <MenuItem value="new">Nuevo</MenuItem>
                                        <MenuItem value="used">Usado</MenuItem>
                                        <MenuItem value="reconditioned">Reacondicionado</MenuItem>
                                    </TextField>
                                </Grid>
                            </Grid>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Button onClick={() => setSeriesDialogOpen(false)} color="inherit">
                        Cancelar
                    </Button>
                    <Button onClick={handleSaveSeries} variant="contained" color="success" startIcon={<CheckIcon />}>
                        Guardar Series ({tempSeries.length})
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default NewPurchase;